/**
 * useNavCards
 *
 * Replaces the window.location.pathname branching in build_practice.js.
 * Each page entry file passes its own static card config — no URL sniffing needed.
 *
 * Usage (in a page's main.js):
 *
 *   import { buildPracticeCards } from '@/composables/useNavCards'
 *   // pass cards as a prop to NavCardList
 *
 * Or use the pre-built configs exported below directly as props.
 */

import practiceImg       from '../media/icons/practice_img.png'
import webFactoryImg     from '../media/icons/build_websites.png'
import transitionImg     from '../media/icons/transition_img.png'
import pythonFactoryImg  from '../media/icons/python_practice.png'
import jsFactoryImg      from '../media/icons/js_practice.png'
import musicImg          from '../media/icons/music_img.png'
import artImg            from '../media/icons/art_img.png'
import musicArtImg       from '../media/icons/musicxart_img.png'
import p5Img             from '../media/icons/p5.png'
import vernaticsImg      from '../media/icons/vernatics_factory.png'
import transformationImg from '../media/icons/transformation.png'
import { navigationIcons, homePageImages } from '../scripts/img_sound_imports.js'
import { currLang } from '../scripts/translation.js'
import { getSelectedBoardVersion, setSelectedBoardVersion } from '../scripts/UI/boardChoiceModal.js'

// ---------------------------------------------------------------------------
// home.html  (index.js)
// ---------------------------------------------------------------------------
export function getHomeCards() {
  return [
    { href: './level_home.html',          bgImage: navigationIcons.learn,   label: currLang['Home.labelLearn'],    sublabel: currLang['Home.labelLearn2']    },
    { href: './transitionxart.html',      bgImage: navigationIcons.practice, label: currLang['Home.labelPractice'], sublabel: currLang['Home.labelPractice2'] },
    { href: './activities_published.html',bgImage: navigationIcons.explore,  label: currLang['Home.labelVisit'],    sublabel: currLang['Home.labelVisit2']    },
    { href: './activities_saved.html',    bgImage: navigationIcons.visit,    label: currLang['Home.labelExplore'],  sublabel: currLang['Home.labelExplore2']  },
  ]
}

// ---------------------------------------------------------------------------
// level_home.html
// ---------------------------------------------------------------------------
export function getLevelHomeCards() {
  const levels = [
    './dragndrop.html', './sequence.html', './loops.html', './loops2.html',
    './events.html', './conditionals.html', './nested_loops.html',
    './functions.html', './variables.html', './data_Simulation.html',
  ]
  const labels = Array.from({ length: 10 }, (_, i) => currLang[`Home.LevelLabel${i + 1}`])

  return levels.map((href, i) => ({
    href,
    label:      String(i + 1),
    sublabel:   labels[i],
    extraClass: i === 9 ? 'last-element' : '',
  }))
}

// ---------------------------------------------------------------------------
// build_practice.html
// ---------------------------------------------------------------------------
export function getBuildPracticeCards() {
  return [
    { href: './factory.html',         bgImage: practiceImg,      label: currLang['Home.labelFactory'],    sublabel: currLang['Home.labelPractice2'] },
    { href: './web_factory.html',     bgImage: webFactoryImg,    label: currLang['Home.labelWeb2'],       sublabel: currLang['Home.labelPractice2'] },
    { href: './python_factory.html',  bgImage: pythonFactoryImg, label: currLang['Home.labelPython'],     sublabel: currLang['Home.labelPractice2'] },
    { href: './js_factory.html',      bgImage: jsFactoryImg,     label: currLang['Home.labelJSFactory'],  sublabel: currLang['Home.labelJavascript'] },
  ]
}

// ---------------------------------------------------------------------------
// transitionxart.html
// ---------------------------------------------------------------------------
export function getTransitionxartCards() {
  const cards = [
    { href: './build_practice.html', bgImage: transitionImg,     label: currLang['TransitionPage.Hybrid'],    sublabel: '' },
    { href: './musicxart.html',      bgImage: musicArtImg,       label: currLang['TransitionPage.ArtMusic'],  sublabel: '' },
    { href: './transformation.html', bgImage: transformationImg, label: currLang['TransitionPage.Transform'], sublabel: '' },
  ]
  // Vernatics only shown on non-portal servers (original logic preserved)
  if (!window.location.hostname.includes('portal')) {
    cards.push({ href: './vernatics.html', bgImage: vernaticsImg, label: currLang['TransitionPage.Vernatics'], sublabel: '' })
  }
  return cards
}

// ---------------------------------------------------------------------------
// transformation.html
// ---------------------------------------------------------------------------
export function getTransformationCards() {
  return [
    { href: './pythoncompiler.html', bgImage: pythonFactoryImg, label: currLang['Home.labelPython'],     sublabel: currLang['Compiler.Text'] },
    { href: './jscompiler.html',     bgImage: jsFactoryImg,     label: currLang['Home.labelJavascript'], sublabel: currLang['Compiler.Text'] },
    { href: './sketch.html',         bgImage: p5Img,            label: 'P5JS',                           sublabel: currLang['Create.Text']   },
  ]
}

// ---------------------------------------------------------------------------
// vernatics.html
// The original had a special onclick="sessionStorage.setItem('vernaticsAuth',true)"
// on the last card. We handle that with an onClick function instead.
//
// The ESP card also carries a board-version dropdown (Board V1 / Board V2).
// The selection is persisted to localStorage (via boardChoiceModal.js, the
// same store the Blockly code generators read from) and is also appended
// as a `?level=` query param when navigating to vernatics_esp.html. `level`
// (not a custom `board` param) is used deliberately: it's the same query
// param the save/share-activity API (shareFeature.js `sendToDatabase`)
// already reads off the URL and stores verbatim on the share record, so a
// saved/shared vernatics_esp activity carries its board version through
// that existing field for free — no backend schema change needed. The page
// itself still applies the right pin configuration from this param without
// re-reading localStorage.
// ---------------------------------------------------------------------------
export function getVernaticsCards() {
  const setAuth = () => sessionStorage.setItem('vernaticsAuth', true)
  const goToEsp = (event) => {
    event.preventDefault()
    window.location.href = `./vernatics_esp.html?level=${getSelectedBoardVersion()}`
  }
  return [
    {
      href: './vernatics_esp.html',
      bgImage: vernaticsImg,
      label: 'ESP',
      sublabel: '',
      onClick: goToEsp,
      select: {
        value: getSelectedBoardVersion(),
        options: [
          { value: 'v1', label: 'Board V1' },
          { value: 'v2', label: 'Board V2' },
        ],
        onChange: setSelectedBoardVersion,
      },
    },
    { href: './vernatics_factory.html', bgImage: vernaticsImg, label: 'PI',  sublabel: '', onClick: setAuth },
  ]
}

// ---------------------------------------------------------------------------
// musicxart.html
// ---------------------------------------------------------------------------
export function getMusicxartCards() {
  return [
    { href: './art_factory.html',   bgImage: artImg,   label: currLang['MusicArt.Art'],   sublabel: currLang['Home.labelPractice2'] },
    { href: './music_factory.html', bgImage: musicImg,  label: currLang['MusicArt.Music'], sublabel: currLang['Home.labelPractice2'] },
  ]
}